import { FC, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Button } from "@/components/button";
import { Box, Flex, Select, Text, TextField } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { useNavigate } from "react-router-dom";
import type {
  GeneratePulseSmartViewRequestSchema,
  GenerateSmartViewRequestSchema,
  SmartViewKind,
  SmartViewSchema,
} from "@/domains/openapi/types";
import { isEmpty } from "lodash-es";
import {
  computeSmartContextId,
  DEFAULT_SMART_VIEW_KIND,
  SMART_VIEW_KIND_OPTIONS,
} from "@/pages/utilities/smart-views/shared/types";

export const SmartViewsGetOrGeneratePage: FC = () => {
  const navigate = useNavigate();
  const [spaceAccountId, setSpaceAccountId] = useState("");
  const [activeNoteId, setActiveNoteId] = useState("");
  const [activeCollectionId, setActiveCollectionId] = useState("");
  const [query, setQuery] = useState("");
  const [continuationId, setContinuationId] = useState("");
  const [smartViewKind, setSmartViewKind] = useState<SmartViewKind>(DEFAULT_SMART_VIEW_KIND);

  const spacesQuery = useQuery({
    queryKey: ["spaces", "me"],
    queryFn: () => api.get("/v1/me/spaces", {}),
  });
  const spaceAccountsQuery = useQuery({
    queryKey: ["space-accounts", "me"],
    queryFn: () => api.get("/v1/me/space-accounts", {}),
  });

  const primarySpaceAccountId = useMemo(() => {
    const spaces = spacesQuery.data?.data as
      | { id: string; is_primary_space?: boolean }[]
      | undefined;
    const spaceAccounts = spaceAccountsQuery.data?.data as
      | { id: string; space_id?: string | null }[]
      | undefined;
    if (!spaces || !spaceAccounts) {
      return null;
    }
    const primarySpace = spaces.find(space => space.is_primary_space);
    if (!primarySpace) {
      return null;
    }
    const primarySpaceAccount = spaceAccounts.find(account => account.space_id === primarySpace.id);
    return primarySpaceAccount?.id ?? null;
  }, [spaceAccountsQuery.data?.data, spacesQuery.data?.data]);

  const isLoadingPrimarySpaceAccount = spacesQuery.isLoading || spaceAccountsQuery.isLoading;
  const isPulseKind = smartViewKind === "PULSE";

  const buildRequestPayload = () => {
    const trimmedSpaceAccountId = spaceAccountId.trim();
    const trimmedActiveNoteId = activeNoteId.trim();
    const trimmedActiveCollectionId = activeCollectionId.trim();
    const trimmedQuery = query.trim();
    const trimmedContinuationId = continuationId.trim();

    const smartContextId = computeSmartContextId({
      spaceAccountId: trimmedSpaceAccountId,
      activeNoteId: trimmedActiveNoteId || undefined,
      activeCollectionId: trimmedActiveCollectionId || undefined,
      query: trimmedQuery || undefined,
      continuationId: trimmedContinuationId || undefined,
      smartViewKind,
    });

    const body: GenerateSmartViewRequestSchema = {
      associated_space_account_id: trimmedSpaceAccountId,
      context: {
        space_account_id: trimmedSpaceAccountId,
        ...(trimmedActiveNoteId ? { active_note_id: trimmedActiveNoteId } : {}),
        ...(trimmedActiveCollectionId ? { active_collection_id: trimmedActiveCollectionId } : {}),
        ...(trimmedQuery ? { query: trimmedQuery } : {}),
        ...(trimmedContinuationId ? { continuation_id: trimmedContinuationId } : {}),
        smart_view_kind: smartViewKind,
      },
    };

    return { smartContextId, body };
  };

  const buildPulseRequestPayload = () => {
    const trimmedSpaceAccountId = spaceAccountId.trim();
    const trimmedActiveCollectionId = activeCollectionId.trim();
    const trimmedQuery = query.trim();

    const smartContextId = computeSmartContextId({
      spaceAccountId: trimmedSpaceAccountId,
      activeCollectionId: trimmedActiveCollectionId || undefined,
      query: trimmedQuery || undefined,
      smartViewKind: "PULSE",
    });

    const body: GeneratePulseSmartViewRequestSchema = {
      query: trimmedQuery,
      ...(trimmedActiveCollectionId ? { active_collection_id: trimmedActiveCollectionId } : {}),
    };

    return { smartContextId, body };
  };

  const navigateToView = (view: SmartViewSchema) => {
    navigate(atlasRoutes.smartViewsView({ params: { smartViewId: view.id } }).path);
  };

  const getOrGenerateMutation = useMutation({
    mutationFn: async () => {
      const { smartContextId, body } = buildRequestPayload();
      const resp = await api.post(
        "/internal/v1/smart-views/by-context/{smart_context_id}/get-or-generate",
        {
          params: { path: { smart_context_id: smartContextId } },
          body,
        }
      );
      return resp.data!;
    },
    onSuccess: navigateToView,
  });

  const generateOnlyMutation = useMutation({
    mutationFn: async () => {
      const { smartContextId, body } = buildRequestPayload();
      const resp = await api.post(
        "/internal/v1/smart-views/by-context/{smart_context_id}/generate",
        {
          params: { path: { smart_context_id: smartContextId } },
          body,
        }
      );
      return resp.data!;
    },
    onSuccess: navigateToView,
  });

  const generatePulseMutation = useMutation({
    mutationFn: async () => {
      const { body } = buildPulseRequestPayload();
      const resp = await api.post("/v2/smart-views/pulse/generate", {
        body,
      });
      return resp.data!;
    },
    onSuccess: navigateToView,
  });

  const handleFillSpaceAccountId = () => {
    if (primarySpaceAccountId) {
      setSpaceAccountId(primarySpaceAccountId);
    }
  };

  const handleGetOrGenerate = () => {
    if (isPulseKind) {
      generatePulseMutation.mutate();
      return;
    }
    getOrGenerateMutation.mutate();
  };

  const handleGenerateOnly = () => {
    if (isPulseKind) {
      generatePulseMutation.mutate();
      return;
    }
    generateOnlyMutation.mutate();
  };

  const canGenerate = useMemo(() => {
    if (isEmpty(spaceAccountId.trim())) {
      return false;
    }
    if (isPulseKind) {
      return query.trim().length > 0;
    }
    return true;
  }, [isPulseKind, query, spaceAccountId]);

  const isMutating =
    getOrGenerateMutation.isPending ||
    generateOnlyMutation.isPending ||
    generatePulseMutation.isPending;

  return (
    <div>
      <h1>Generate Smart View</h1>

      <CardBox>
        <Flex direction="column" gap="3">
          <Text as="div" size="2" weight="bold">
            Get or Generate Smart View
          </Text>
          <FieldRow>
            <Label>associated_space_account_id</Label>
            <Flex gap="2" align="center" style={{ width: "100%" }}>
              <StyledTextField
                placeholder="5ad2a64b-..."
                value={spaceAccountId}
                onChange={e => setSpaceAccountId(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                type="button"
                variant="soft"
                onClick={handleFillSpaceAccountId}
                disabled={!primarySpaceAccountId || isLoadingPrimarySpaceAccount}
              >
                Fill my space account id
              </Button>
            </Flex>
          </FieldRow>
          <FieldRow>
            <Label>context.active_note_id</Label>
            <StyledTextField
              placeholder="f93a26a6-... (optional)"
              value={activeNoteId}
              onChange={e => setActiveNoteId(e.target.value)}
              disabled={isPulseKind}
            />
          </FieldRow>
          <FieldRow>
            <Label>context.active_collection_id</Label>
            <StyledTextField
              placeholder="c29d7d1b-... (optional)"
              value={activeCollectionId}
              onChange={e => setActiveCollectionId(e.target.value)}
            />
          </FieldRow>
          <FieldRow>
            <Label>context.query</Label>
            <StyledTextField
              placeholder={
                isPulseKind ? "Project Pulse (required for Pulse)" : "project alpha (optional)"
              }
              value={query}
              onChange={e => setQuery(e.target.value)}
              required={isPulseKind}
            />
          </FieldRow>
          <FieldRow>
            <Label>context.continuation_id</Label>
            <StyledTextField
              placeholder="fa8170b4-... (optional)"
              value={continuationId}
              onChange={e => setContinuationId(e.target.value)}
              disabled={isPulseKind}
            />
          </FieldRow>
          <FieldRow>
            <Label>context.smart_view_kind</Label>
            <Select.Root
              value={smartViewKind}
              onValueChange={value => setSmartViewKind(value as SmartViewKind)}
            >
              <Select.Trigger />
              <Select.Content>
                {SMART_VIEW_KIND_OPTIONS.map(kind => (
                  <Select.Item key={kind} value={kind}>
                    {kind}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </FieldRow>
          <Flex gap="2" align="center">
            <Button onClick={handleGetOrGenerate} disabled={!canGenerate || isMutating}>
              {isPulseKind
                ? generatePulseMutation.isPending
                  ? "Generating…"
                  : "Generate Pulse"
                : getOrGenerateMutation.isPending
                  ? "Processing…"
                  : "Get or Generate"}
            </Button>
            {!isPulseKind && (
              <Button
                variant="soft"
                onClick={handleGenerateOnly}
                disabled={!canGenerate || isMutating}
              >
                {generateOnlyMutation.isPending ? "Generating…" : "Generate"}
              </Button>
            )}
          </Flex>
        </Flex>
      </CardBox>
    </div>
  );
};

const StyledTextField = styled(TextField.Root)`
  width: 100%;
`;

const CardBox = styled(Box)`
  background: var(--gray-2);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--gray-4);
  max-width: 720px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled(Text)`
  font-weight: 500;
  color: var(--gray-11);
`;

export default SmartViewsGetOrGeneratePage;
